import { useState, useRef } from "react"
import { Download, Upload, FilePlus } from "lucide-react"
import { Toast } from "primereact/toast"
import { ClimbingBoxLoader } from "react-spinners"

const API_BASE_URL = "https://api.rcubedev.tickbox.co.in/"

const Dropdown = ({ label, options, value, onChange, name, disabled }) => (
  <div className="flex flex-col mb-4">
    <label className="text-sm font-semibold mb-1 text-white">
      {label} <span className="text-red-500">*</span>
    </label>
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        required
        disabled={disabled}
        className={`appearance-none bg-white/10 backdrop-blur-lg border border-white/30 text-white rounded-2xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#d97925] shadow-xl w-full cursor-pointer ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <option value="">Select {label}</option>
        {options.map((opt) => (
          <option key={opt} value={opt} className="bg-white text-black">
            {opt}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-white">▼</div>
    </div>
  </div>
)

const InputField = ({ label, name, value, onChange, placeholder, type = "text", disabled }) => (
  <div className="flex flex-col mb-4">
    <label className="text-sm font-semibold mb-1 text-white">
      {label} <span className="text-red-500">*</span>
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={`bg-white/10 backdrop-blur-lg border border-white/30 text-white p-3 rounded-xl placeholder:text-white/70 ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      }`}
    />
  </div>
)

const FileUpload = ({ onFileUpload, uploadedFile, onRemove }) => (
  <div className="mt-6 flex flex-col items-center">
    <label className="text-sm font-semibold mb-1 text-white">
      Upload PNG File <span className="text-red-500">*</span>
    </label>
    <label className="bg-white text-[#d97925] border border-[#d97925] rounded-full p-3 cursor-pointer hover:bg-[#d97925] hover:text-white transition-all shadow-md">
      <FilePlus size={20} />
      <input id="fileInput" type="file" accept=".png" className="hidden" onChange={onFileUpload} />
    </label>
    {uploadedFile && (
      <div className="mt-4 flex items-center gap-3 bg-white/10 backdrop-blur-lg px-4 py-2 rounded-xl shadow text-white">
        <span className="text-sm">{uploadedFile.name}</span>
        <button type="button" onClick={onRemove} className="text-red-400 text-xs font-semibold hover:text-red-600">
          Remove
        </button>
      </div>
    )}
  </div>
)

const dropdownOptions = {
  payrollTypes: ["Live", "Customized", "Bio-Metric"],
}

export const BulkConfig = () => {
  const [zipFile, setZipFile] = useState(null)
  const [excelFile, setExcelFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const toast = useRef(null)

  const handleDownload = async () => {
    const token = sessionStorage.getItem("token")
    try {
      const response = await fetch(`${API_BASE_URL}api/download_config_data/`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      console.log("Status:", response.status)
      if (!response.ok) {
        const errorText = await response.text()
        console.error("Download failed. Server said:", errorText)
        throw new Error("Failed to download template")
      }

      // Extract filename from Content-Disposition header [^vercel_knowledge_base]
      const contentDisposition = response.headers.get("Content-Disposition")
      let filename = "RCube_Config_Template.xlsx" // Default fallback filename

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-8''|)([^;"]+)/i)
        if (filenameMatch && filenameMatch[1]) {
          try {
            filename = decodeURIComponent(
              filenameMatch[1].replace(/%([0-9A-Fa-f]{2})/g, (match, p1) =>
                String.fromCharCode(Number.parseInt(p1, 16)),
              ),
            )
          } catch (e) {
            console.error("Error decoding filename:", e)
            filename = filenameMatch[1] // Use raw filename if decoding fails
          }
        }
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename // Use the extracted filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.current?.show({
        severity: "success",
        summary: "Download Successful",
        detail: "Template downloaded successfully.",
        life: 5000,
      })
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Failed",
        detail: error.message,
        life: 0,
        sticky: true,
        closable: true,
      })
    }
  }

  const handleZipUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.name.endsWith(".zip")) setZipFile(file)
  }

  const handleExcelUpload = (e) => {
    const file = e.target.files[0]
    if (file && file.name.endsWith(".xlsx")) setExcelFile(file)
  }

  const removeZipFile = () => {
    setZipFile(null)
    document.getElementById("zipInput").value = ""
  }

  const removeExcelFile = () => {
    setExcelFile(null)
    document.getElementById("excelInput").value = ""
  }

  const handleBulkSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    if (!zipFile || !excelFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Missing Files",
        detail: "Please upload both .zip and .xlsx files before submitting.",
        life: 0,
        sticky: true,
        closable: true,
      })
      setLoading(false)
      return
    }
    const token = sessionStorage.getItem("token")
    const formData = new FormData()
    formData.append("excel_file", excelFile, excelFile.name)
    formData.append("company_stamp_signatures", zipFile, zipFile.name)
    try {
      const response = await fetch(`${API_BASE_URL}api/client_creation/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })
      if (response.ok) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Bulk Configuration Submitted Successfully!",
          life: 0,
          sticky: true,
          closable: true,
        })
        removeZipFile()
        removeExcelFile()
      } else {
        if (response.status === 500) {
          toast.current?.show({
            severity: "error",
            summary: "Something Went Wrong!!!",
            detail: "Server error (500). Please try again later.",
            life: 4000,
          })
        } else {
          const data = await response.json()
          toast.current?.show({
            severity: "error",
            summary: "Submission Failed",
            detail: data?.message || "Something went wrong.",
            life: 0,
            sticky: true,
            closable: true,
          })
        }
      }
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Network Error",
        detail: error.message,
        life: 3000,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={15} />
        </div>
      )}
      <form
        onSubmit={handleBulkSubmit}
        className="w-full bg-white/10 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl p-6 text-white"
      >
        <Toast ref={toast} />
        <h2 className="text-2xl font-bold mb-6 text-center">Bulk Configuration</h2>
        <div className="flex flex-col items-center gap-6">
          <div className="flex flex-col items-center">
            <label className="text-sm font-semibold mb-1 text-white">Download Client REG Template</label>
            <button
              type="button"
              onClick={handleDownload}
              className="bg-white text-[#d97925] border border-[#d97925] rounded-full p-3 hover:bg-[#d97925] hover:text-white transition-all shadow-md"
            >
              <Download size={20} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4 w-full max-w-lg">
            {/* Excel Upload */}
            <div className="flex flex-col items-center">
              <label className="text-sm font-semibold mb-1 text-white">
                Upload Excel File (.xlsx) <span className="text-red-500">*</span>
              </label>
              <label className="bg-white text-[#d97925] border border-[#d97925] rounded-full p-3 cursor-pointer hover:bg-[#d97925] hover:text-white transition-all shadow-md">
                <FilePlus size={20} />
                <input id="excelInput" type="file" accept=".xlsx" className="hidden" onChange={handleExcelUpload} />
              </label>
              {excelFile && (
                <div className="mt-3 flex items-center gap-3 bg-white/10 backdrop-blur-lg px-4 py-2 rounded-xl shadow text-white w-full justify-between max-w-xs">
                  <span className="text-sm truncate">{excelFile.name}</span>
                  <button
                    type="button"
                    onClick={removeExcelFile}
                    className="text-red-400 text-xs font-semibold hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            {/* ZIP Upload */}
            <div className="flex flex-col items-center">
              <label className="text-sm font-semibold mb-1 text-white">
                Upload ZIP File (.zip) <span className="text-red-500">*</span>
              </label>
              <label className="bg-white text-[#d97925] border border-[#d97925] rounded-full p-3 cursor-pointer hover:bg-[#d97925] hover:text-white transition-all shadow-md">
                <Upload size={20} />
                <input id="zipInput" type="file" accept=".zip" className="hidden" onChange={handleZipUpload} />
              </label>
              {zipFile && (
                <div className="mt-3 flex items-center gap-3 bg-white/10 backdrop-blur-lg px-4 py-2 rounded-xl shadow text-white w-full justify-between max-w-xs">
                  <span className="text-sm truncate">{zipFile.name}</span>
                  <button
                    type="button"
                    onClick={removeZipFile}
                    className="text-red-400 text-xs font-semibold hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>
          <button
            type="submit"
            className="bg-[#d97925] text-black px-8 py-3 rounded-xl font-semibold shadow-lg hover:scale-105 transition-all"
          >
            Submit
          </button>
        </div>
      </form>
    </>
  )
}

export default function ClientConfiguration() {
  const [formData, setFormData] = useState({
    clientName: "",
    payrollType: "",
    userId: "",
    name: "",
    email: "",
    mobile: "",
    preclosure: false,
  })
  const [uploadedFile, setUploadedFile] = useState(null)
  const [loading, setLoading] = useState(false)
  const toast = useRef(null)

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleFileUpload = (e) => {
    const file = e.target.files[0]
    if (file) setUploadedFile(file)
  }

  const handleRemove = () => {
    setUploadedFile(null)
    const fileInput = document.getElementById("fileInput")
    if (fileInput) {
      // Add this check
      fileInput.value = ""
    }
  }

  const handleSPOCSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    // Validate file presence only if payrollType is not "Bio-Metric"
    if (formData.payrollType !== "Bio-Metric" && !uploadedFile) {
      if (toast.current) {
        toast.current.show({
          severity: "warn",
          summary: "Missing File",
          detail: "Please upload a .png file before submitting.",
          life: 6000,
        })
      }
      setLoading(false)
      return
    }
    const token = sessionStorage.getItem("token")
    // Prepare FormData
    const formDataToSend = new FormData()
    formDataToSend.append("Company_Name", formData.clientName)
    formDataToSend.append("Client_SPOC_ID", formData.userId)
    formDataToSend.append("Client_SPOC_Name", formData.name)
    formDataToSend.append("Client_SPOC_Mail_ID", formData.email)
    formDataToSend.append("Client_SPOC_Mobile_Number", formData.mobile)
    formDataToSend.append("Pre_Closure_Type", formData.preclosure ? "Yes" : "No")
    formDataToSend.append("Payroll_Type", formData.payrollType)

    // Only append PNG file if payrollType is not "Bio-Metric"
    if (formData.payrollType !== "Bio-Metric" && uploadedFile) {
      formDataToSend.append("company_stamp_signature", uploadedFile, uploadedFile.name)
      formDataToSend.append("logo", uploadedFile, uploadedFile.name)
    }

    try {
      const response = await fetch(`${API_BASE_URL}api/client_creation/`, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          // DO NOT SET 'Content-Type' manually — browser will handle it
        },
        body: formDataToSend,
      })
      if (!response.ok) {
        const errorText = await response.text()
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: `Server Error: ${response.status} - ${errorText}`,
            life: 4000,
          })
        }
        throw new Error(`Server Error: ${response.status} - ${errorText}`)
      }
      //const result = await response.json();
      if (toast.current) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Client SPOC Configuration Submitted Successfully!",
          life: 3000,
        })
      }
      // Reset form
      setFormData({
        clientName: "",
        payrollType: "",
        userId: "",
        name: "",
        email: "",
        mobile: "",
        preclosure: false,
      })
      handleRemove() // remove uploaded file
    } catch (error) {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "An error occurred during submission.",
          life: 0,
          sticky: true,
          closable: true,
        })
      }
      console.error("Submission Error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleClear = () => {
    setFormData({
      clientName: "",
      payrollType: "",
      userId: "",
      name: "",
      email: "",
      mobile: "",
      preclosure: false,
    })
    handleRemove()
  }

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={15} />
        </div>
      )}
      <div className="flex flex-col lg:flex-row gap-8">
        <Toast ref={toast} />
        {/* SPOC Section */}
        <form
          onSubmit={handleSPOCSubmit}
          className="w-full lg:w-1/2 bg-white/10 backdrop-blur-xl border border-white/30 rounded-2xl shadow-2xl p-6 text-white"
        >
          <h2 className="text-2xl font-bold mb-6 text-center">Client SPOC Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Client Name"
              placeholder="Enter client name"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
            />
            <Dropdown
              label="Payroll Type"
              options={dropdownOptions.payrollTypes}
              name="payrollType"
              value={formData.payrollType}
              onChange={handleChange}
              disabled={!formData.clientName}
            />
            <InputField
              label="Client SPOC ID"
              placeholder="Enter SPOC ID"
              name="userId"
              value={formData.userId}
              onChange={handleChange}
              disabled={!formData.payrollType}
            />
            <InputField
              label="Name"
              placeholder="Enter name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={!formData.userId}
            />
            <InputField
              label="Email"
              type="email"
              placeholder="Enter email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={!formData.name}
            />
            <InputField
              label="Mobile"
              placeholder="Enter mobile number"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              disabled={!formData.email}
            />
            {/* Preclosure toggle below Mobile input */}
            <div className="flex items-center gap-3 mt-2 md:col-span-2">
              <label className="text-sm font-semibold text-white">Preclosure</label>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, preclosure: !prev.preclosure }))}
                className={`relative w-14 h-8 rounded-full transition-colors duration-300 focus:outline-none ${
                  formData.preclosure ? "bg-[#d97925]" : "bg-white border border-[#d97925]"
                }`}
                aria-pressed={formData.preclosure}
              >
                <span
                  className={`absolute top-1 w-6 h-6 rounded-full shadow-md transform transition-transform duration-300 ${
                    formData.preclosure ? "left-1 bg-white translate-x-6" : "left-1 bg-black translate-x-0"
                  }`}
                />
              </button>
            </div>
            {formData.payrollType !== "Bio-Metric" && (
              <div className="md:col-span-2 flex justify-center">
                <FileUpload onFileUpload={handleFileUpload} uploadedFile={uploadedFile} onRemove={handleRemove} />
              </div>
            )}
          </div>
          <div className="flex justify-center mt-8 gap-6">
            <button
              type="submit"
              className="bg-[#d97925] text-black px-8 py-3 rounded-xl font-semibold shadow-lg hover:scale-105 transition-all"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="bg-white/10 text-white border border-white/30 px-8 py-3 rounded-xl font-semibold shadow-lg hover:scale-105 hover:bg-red-600 transition-all"
            >
              Clear
            </button>
          </div>
        </form>
        {/* Bulk Section */}
        <div className="w-full lg:w-1/2">
          <BulkConfig />
        </div>
      </div>
    </>
  )
}
